import DOMPurify from "dompurify";
import { Marked } from "marked";
import { ATTACHMENT_IMAGE, type InlineMedia } from "@ccnote/shared";

// GitHub-flavoured Markdown (tables, task lists, strikethrough). Single line breaks are kept,
// since people write notes line by line rather than in paragraphs.
const marked = new Marked({ gfm: true, breaks: true });

// Links in notes open in a new tab and don't leak this page to the target.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});


/**
 * Markdown → HTML that is safe to put in the page (scripts, event handlers and javascript: links removed).
 * `media` resolves ![alt](attachment:NAME) to the note's stored pictures and videos. Videos play in
 * place (or, `forPrint`, become a link). Unknown names show their alt text, if any.
 */
export function renderMarkdown(source: string, media: InlineMedia = {}, { forPrint = false } = {}): string {
  const withMedia = source.replace(ATTACHMENT_IMAGE, (_, alt: string, name: string) => {
    const item = media[name];
    if (!item) return alt ? alt.replace(/[[\]]/g, "") : "";
    if (!item.video) return `![${alt}](${item.url})`;
    return forPrint ? `[🎬 Video](${item.url})` : `\n\n<video controls preload="metadata" playsinline src="${item.url.replace(/"/g, "%22")}"></video>\n\n`;
  });
  const html = marked.parse(withMedia, { async: false });
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true }, ADD_TAGS: ["video"], ADD_ATTR: ["controls", "preload", "playsinline"] });
}

export { inlineAttachmentNames, markdownToPlain, markdownToText, toggleTask } from "@ccnote/shared";
